import { isRedSuit, type PokerCard, SUIT_SYMBOL } from "@/lib/poker-cards";
import type { Suit } from "@/lib/flush-visual";
import { cn } from "@/lib/cn";

/**
 * 同花主导花色高亮底色(方案丁·精简):按花色淡彩 + 柔光,克制不刺眼。
 * ♠ 淡蓝 · ♥ 淡红 · ♦ 淡橙 · ♣ 淡绿;牌桌主角仍是牌,高亮只是聚光。
 */
const HIGHLIGHT_TINT: Record<Suit, string> = {
  s: "bg-sky-100 ring-2 ring-sky-400/70 shadow-[0_0_12px_rgba(56,189,248,0.45)]",
  h: "bg-rose-100 ring-2 ring-rose-400/70 shadow-[0_0_12px_rgba(251,113,133,0.45)]",
  d: "bg-orange-100 ring-2 ring-orange-400/70 shadow-[0_0_12px_rgba(251,146,60,0.45)]",
  c: "bg-emerald-100 ring-2 ring-emerald-400/70 shadow-[0_0_12px_rgba(52,211,153,0.45)]",
};

export function PlayingCard({
  card,
  size = "md",
  highlight = false,
}: {
  card: PokerCard;
  size?: "sm" | "md" | "lg";
  /** 同花主导花色高亮:淡彩底 + 柔光 ring + 轻微抬升(仅真实花色生效)。 */
  highlight?: boolean;
}) {
  const red = isRedSuit(card.suit);
  const unknown = card.suit === "x";
  const litSuit: Suit | null =
    card.suit !== "x" && highlight ? card.suit : null;
  return (
    <span
      className={cn(
        "inline-flex select-none flex-col items-center justify-center rounded-md font-semibold shadow-md ring-1 ring-zinc-950/20 transition-all duration-300",
        size === "lg" && "h-16 w-12 text-xl",
        size === "md" && "h-12 w-9 text-sm",
        size === "sm" && "h-8 w-6 text-[10px]",
        unknown
          ? "bg-zinc-300/80 text-zinc-600"
          : cn("bg-zinc-50", red ? "text-rose-600" : "text-zinc-900"),
        litSuit && HIGHLIGHT_TINT[litSuit],
        litSuit && "-translate-y-0.5",
      )}
    >
      <span className="leading-tight">{card.rank}</span>
      {!unknown && <span className="leading-tight">{SUIT_SYMBOL[card.suit]}</span>}
    </span>
  );
}

/** 解析成功渲染扑克牌;解析失败原样文本兜底 */
export function CardRow({
  cards,
  raw,
  size = "md",
  highlightSuit = null,
}: {
  cards: PokerCard[] | null;
  raw?: string | null;
  size?: "sm" | "md" | "lg";
  /** 同花主导花色:该花色所有牌高亮(null = 无同花语义,不高亮)。 */
  highlightSuit?: Suit | null;
}) {
  if (!cards || cards.length === 0) {
    if (!raw) return null;
    return (
      <span className="rounded-lg bg-zinc-950/60 px-2.5 py-1 font-mono text-sm text-zinc-200">
        {raw}
      </span>
    );
  }
  return (
    <span className="inline-flex gap-1.5">
      {cards.map((c, i) => (
        <PlayingCard
          key={`${c.rank}${c.suit}-${i}`}
          card={c}
          size={size}
          highlight={highlightSuit !== null && c.suit === highlightSuit}
        />
      ))}
    </span>
  );
}
